type CacheEntry = {
    url: string;
    // Unix epoch seconds parsed from the signed URL's `?Expires=` query
    // parameter. `null` when the URL has no expiry (e.g. legacy public
    // URL that the resolver returned unchanged).
    expiresAt: number | null;
};

type PendingPromise = Promise<string>;

const SAFETY_MARGIN_SECONDS = 10;

/**
 * Shared cache for signed URLs returned by backend resolver endpoints.
 *
 * Keys are stable strings produced by callers (e.g. `audio:42`). The
 * cache deduplicates concurrent requests for the same key — only the
 * first caller hits the network, every other caller awaits the same
 * promise.
 *
 * URL freshness is determined by parsing `?Expires=` out of the signed
 * URL itself: callers never need to track lifetimes manually.
 */
export class SignedUrlCache {
    private entries = new Map<string, CacheEntry>();

    private pending = new Map<string, PendingPromise>();

    get(key: string): CacheEntry | undefined {
        const entry = this.entries.get(key);
        if (!entry) return undefined;
        if (entry.expiresAt && Date.now() / 1000 >= entry.expiresAt - SAFETY_MARGIN_SECONDS) {
            this.entries.delete(key);
            return undefined;
        }
        return entry;
    }

    set(key: string, url: string): CacheEntry {
        const entry: CacheEntry = {
            url,
            expiresAt: parseExpires(url),
        };
        this.entries.set(key, entry);
        return entry;
    }

    invalidate(key: string): void {
        this.entries.delete(key);
    }

    clear(): void {
        this.entries.clear();
        this.pending.clear();
    }

    /**
     * Resolves a URL through the loader, returning a cached value when
     * available and deduplicating concurrent in-flight requests.
     */
    async resolve(key: string, loader: () => Promise<string>): Promise<string> {
        const cached = this.get(key);
        if (cached) {
            return cached.url;
        }

        const inflight = this.pending.get(key);
        if (inflight) {
            return inflight;
        }

        const request = loader()
            .then((url) => {
                this.set(key, url);
                return url;
            })
            .finally(() => {
                this.pending.delete(key);
            });

        this.pending.set(key, request);
        return request;
    }
}

export function parseExpires(url: string | null | undefined): number | null {
    if (typeof url !== 'string') return null;
    // Match both CloudFront (`Expires=`) and Laravel signed routes (`expires=`).
    // The `twilioRecording` resolver returns a Laravel temporarySignedRoute URL
    // whose query parameter is lowercase — without the case-insensitive flag we
    // would treat those URLs as never-expiring and serve stale links forever.
    const match = url.match(/[?&]expires=(\d+)/i);
    return match ? Number(match[1]) : null;
}

/**
 * Returns true when the given signed URL has reached (or is within
 * the safety margin of) its `?Expires=` cutoff. URLs without an
 * Expires query parameter are considered fresh.
 */
export function isSignedUrlExpired(url: string | null | undefined): boolean {
    const expiresAt = parseExpires(url);
    if (!expiresAt) return false;
    return Date.now() / 1000 >= expiresAt - SAFETY_MARGIN_SECONDS;
}

export const SIGNED_URL_SAFETY_MARGIN_SECONDS = SAFETY_MARGIN_SECONDS;
